import { useEffect, useMemo, useState } from "react";
import { Film } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { VideoCard } from "@/components/site/VideoCard";
import { CtaButton } from "@/components/site/Header";
import { Squiggle, Spiral, Star } from "@/components/site/Doodles";
import { api } from "@/lib/api";

const EmptyState = () => (
  <div className="relative" data-testid="portfolio-empty-state">
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="relative grid aspect-video place-items-center rounded-3xl border-2 border-dashed border-purple-500/40 bg-[var(--km-card)]/50" data-testid={`portfolio-empty-slot-${i}`}>
          <Film className="km-float h-10 w-10 text-[var(--km-purple)]" style={{ animationDelay: `${i * 0.6}s` }} />
          <span className="font-head absolute bottom-3 right-4 text-xs text-purple-300/60">مكان فيديو جاي</span>
        </div>
      ))}
    </div>
    <div className="relative mx-auto -mt-10 max-w-xl rounded-[2rem] border border-purple-500/30 bg-[var(--km-bg2)] p-8 text-center shadow-[0_20px_60px_rgba(0,0,0,0.6)] sm:-mt-24">
      <Spiral className="km-spin-slow mx-auto mb-4 h-16 w-16" />
      <h2 className="font-display text-3xl text-white sm:text-4xl">لسه بنخربش على أول فيديو هنا!</h2>
      <p className="mt-3 text-purple-200/85">قريب جداً هتلاقي هنا أحدث شغلنا. ولحد ما ده يحصل، ممكن يكون إعلانك هو أول واحد يتعرض.</p>
      <CtaButton className="mt-6" testId="portfolio-empty-create-ad-btn" />
    </div>
  </div>
);

export default function Portfolio() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("الكل");

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const cats = useMemo(() => ["الكل", ...new Set((items || []).map((i) => i.category).filter(Boolean))], [items]);
  const shown = (items || []).filter((i) => cat === "الكل" || i.category === cat);

  return (
    <div data-testid="portfolio-page">
      <Seo title="شغلنا" description="شوف أحدث إعلانات وفيديوهات الموشن جرافيك من استوديو خرابيش موشن." />
      <section className="relative mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 md:pt-24 lg:px-8">
        <Star className="km-float absolute left-8 top-16 h-10 w-10" />
        <Reveal>
          <p className="font-head mb-3 text-[var(--km-cyan)]">البورتفوليو</p>
          <h1 className="font-display text-5xl text-white sm:text-6xl lg:text-7xl">
            شغلنا <span className="relative inline-block text-[var(--km-pink)]">بيتكلم<Squiggle className="absolute -bottom-3 right-0 h-5 w-full" color="#E9D8FF" /></span>
          </h1>
        </Reveal>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        {items === null ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolio-loading">
            {[0, 1, 2].map((i) => <div key={i} className="aspect-video animate-pulse rounded-3xl bg-[var(--km-card)]" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {cats.length > 2 && (
              <div className="mb-8 flex flex-wrap gap-2" role="tablist">
                {cats.map((c) => (
                  <button key={c} onClick={() => setCat(c)} role="tab" aria-selected={cat === c} data-testid={`portfolio-filter-${c}`}
                    className={`font-head rounded-full px-5 py-2 text-sm font-bold transition-colors duration-200 ${cat === c ? "bg-[var(--km-pink)] text-white" : "border border-purple-500/30 text-[var(--km-lilac)] hover:text-white"}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolio-grid">
              {shown.map((item, i) => (
                <Reveal key={item.id} delay={(i % 3) * 0.08}><VideoCard item={item} index={i} /></Reveal>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
